import {
  ActCatalog,
  ActQuestionLog,
  FOCUS_CORRECTNESS,
  FOCUS_PACE,
  FOCUS_STRATEGY,
  FOCUS_TIME,
  TAG_STRATEGY_PLANNED,
  focusHit,
  formatDelta,
  formatDuration,
  strategiesFor,
  tagLabel,
} from '@/lib/actModes'
import { cn } from '@/lib/utils'

/** What the question asked him to call, in three words or fewer. */
function calledLabel(log: ActQuestionLog, catalog: ActCatalog | null): string {
  switch (log.focus) {
    case FOCUS_TIME:
      return formatDuration(log.predictedSeconds)
    case FOCUS_CORRECTNESS:
      return log.predictedCorrect == null ? '—' : log.predictedCorrect ? "I'll get it" : "I'll miss it"
    case FOCUS_STRATEGY: {
      const keys = strategiesFor(log)
      return keys.length > 0 ? tagLabel(catalog, TAG_STRATEGY_PLANNED, keys[0]) : '—'
    }
    case FOCUS_PACE:
      return `${formatDuration(log.targetSeconds)} budget`
    default:
      return '—'
  }
}

/** How that call turned out, scoped to what was called. */
function resultLabel(log: ActQuestionLog): string {
  switch (log.focus) {
    case FOCUS_TIME: return formatDelta(log.timeDeltaSeconds)
    case FOCUS_CORRECTNESS: return log.predictionHit ? 'read it right' : 'read it wrong'
    case FOCUS_PACE: return log.onPace ? 'inside budget' : formatDelta(
      log.actualSeconds != null && log.targetSeconds != null ? log.actualSeconds - log.targetSeconds : null)
    default: return log.actualCorrect ? 'landed it' : 'missed it'
  }
}

/**
 * Every question in one scannable column -- what he called, what it took, and
 * whether it landed. One line each; the point is to see the shape of a round at
 * a glance, not to read an essay per question.
 */
export default function ActLogList({ logs, catalog }: { logs: ActQuestionLog[]; catalog: ActCatalog | null }) {
  if (logs.length === 0) {
    return <p className="text-sm text-muted-foreground">No questions logged yet.</p>
  }

  return (
    <div className="border border-border rounded-xl divide-y divide-border overflow-hidden">
      {logs.map(log => {
        const hit = focusHit(log)
        return (
          <div key={log.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
            <span
              className={cn(
                'w-2 h-8 rounded-full shrink-0',
                log.actualCorrect === true ? 'bg-green-500' : log.actualCorrect === false ? 'bg-red-400' : 'bg-border'
              )}
              aria-hidden="true"
            />
            <span className="w-8 shrink-0 text-xs text-muted-foreground tabular-nums">
              Q{log.questionIndex + 1}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block truncate">{calledLabel(log, catalog)}</span>
              <span className={cn('block text-xs truncate', hit ? 'text-green-700' : 'text-muted-foreground')}>
                {resultLabel(log)}
              </span>
            </span>
            <span className="shrink-0 font-medium tabular-nums">{formatDuration(log.actualSeconds)}</span>
          </div>
        )
      })}
    </div>
  )
}
